/**
 * Standard Foundation form object. If specified, the submitDelegate performs
 * the work of the form submission, instead of the default form submission.
 * This object inherits from Foundation.Promise, and it gets fulfilled
 * only when the promise its submitDelegate function returns gets fulfilled.
 * If there were errors during the submit process, the promise should contain
 * a data property named "errors".
 * @constructor
 * @extends {Foundation.View}
 * @param {jQuerySelector} formElement
 * @param {(function(Object.<string,*>):!Foundation.Promise)=} submitDelegate
 */
Foundation.Form = function (formElement, submitDelegate) {
    Foundation.View.call(this, formElement);

    /**
     * @private
     * @type {jQuery}
     */
    this.submitButton = this.element.find("input[type=submit]");

    /**
     * @private
     * @type {Foundation.View.ErrorList}
     */
    this.errorListView = new Foundation.View.ErrorList(this.element.find("div.errorDisplay"));

    /**
     * @private
     * @type {boolean}
     */
    this.enabled = true;

    /**
     * @private
     * @type {Object.<string,*>}
     */
    this.preservedValues = null;
    this.observe("beforeDisable", function () {
        this.preservedValues = this.values();
    }.bind(this));
    this.observe("enabled", function () {
        this.preservedValues = null;
    }.bind(this));

    /**
     * @private
     * @type {Array.<Foundation.Form.Validator>}
     */
    this.validators = [];
    for (var i = 0; i < Foundation.Form.validators.length; i++) {
        this.validators.push(new Foundation.Form.validators[i](this));
    }

    /**
     * @private
     * @type {function(Object.<string,string>)?}
     */
    this._feelingShyDelegate = null;

    this.element.on("submit.fdnform", this.submitHandler.bind(this));
    this.element.on("change.fdnform", this.changeHandler.bind(this));
    this.setSubmitDelegate(submitDelegate || null);
};
Foundation.inherit(Foundation.Form, Foundation.View);

/**
 * Foundation.Form.Validator constructors.
 * @private
 * @type {Array.<function(new:Foundation.Form.Validator,Foundation.Form)>}
 */
Foundation.Form.validators = [];

/**
 * @param {function(new:Foundation.Form.Validator,Foundation.Form)} validator
 */
Foundation.Form.registerValidator = function (validator) {
    Foundation.Form.validators.push(validator);
};

/**
 * @param {(function(Object.<string,*>):!Foundation.Promise)?} delegate
 */
Foundation.Form.prototype.setSubmitDelegate = function (delegate) {
    this.submitDelegate = delegate;
};

/**
 * @param {function(Object.<string,string>)} delegate
 */
Foundation.Form.prototype.setFeelingShyDelegate = function (delegate) {
    this._feelingShyDelegate = delegate;
};

/**
 * @param {Object.<string,string>} feelingShyData
 */
Foundation.Form.prototype.updateFeelingShyDelegate = function (feelingShyData) {
    if (this._feelingShyDelegate) {
        this._feelingShyDelegate(feelingShyData);
    }
};

/**
 * Promise object for the next time the form is successfully submitted.
 * @return {!Foundation.Promise}
 */
Foundation.Form.prototype.submitted = function () {
    if (!this.submittedPromise) {
        this.submittedPromise = new Foundation.Promise();
        this.submittedPromise.always(function () {
            this.submittedPromise = null;
        }.bind(this));
    }
    return this.submittedPromise;
};

/**
 * Return the values of the form elements.
 * @return {Object.<string,*>}
 */
Foundation.Form.prototype.values = function () {
    // Use the preserved values when available. This is not to speed up access,
    // but is necessary for retrieving values when the form is disabled in
    // order to prevent editing and multiple submissions.
    if (this.preservedValues) return this.preservedValues;

    var vals = {};
    var pairs = this.element.serializeArray();
    for (var i = 0; i < pairs.length; i++) {
        var name = pairs[i]["name"];
        var value = pairs[i]["value"];

        if (vals[name]) {
            if (!jQuery.isArray(vals[name])) {
                var singleVal = vals[name];
                vals[name] = [singleVal];
            }
            vals[name].push(value);
        }
        else {
            vals[name] = value;
        }
    }
    return vals;
};

/**
 * Disable form submission and show a spinner after a delay. Or reenable a
 * the form and remove the spinner.
 * @param {boolean} isBusy
 */
Foundation.Form.prototype.setBusyState = function (isBusy) {
    if (isBusy) {
        this.disable();
        this.errorListView.clear();
        if (!this.spinner) {
            this.spinner = new Foundation.View.Spinner(this.element[0], {"delay": 0.5});
        }
        else {
            this.spinner.show();
        }
    }
    else {
        this.enable();
        if (this.spinner) {
            this.spinner.hide();
            this.spinner = null;
        }
    }
};


/** @const Form elements that can be disabled/enabled. */
Foundation.Form.DISABLEABLES = "button,input,select,textarea";

/**
 * Disable all the elements of a form. Keep track of what we're disabling
 * and what was already disabled so that re-enabling the form restores
 * the previous disabled/enabled state of each element.
 */
Foundation.Form.prototype.disable = function () {
    if (this.enabled) {
        this.fire("beforeDisable");
        var formElems = this.element.find(Foundation.Form.DISABLEABLES);
        var formElemsToDisable = formElems.filter(":enabled");
        formElemsToDisable.prop("disabled", true);
        formElemsToDisable.data("disabledByFoundationForm", true);
        this.enabled = false;
        this.fire("disabled");
    }
};

/**
 * Enable all the previously disabled elements of a form.
 */
Foundation.Form.prototype.enable = function () {
    if (!this.enabled) {
        this.fire("beforeEnable");
        var formElems = this.element.find(Foundation.Form.DISABLEABLES)
        var formElemsToEnable = formElems.filter(function () {
            return jQuery(this).data("disabledByFoundationForm") || false;
        });
        formElemsToEnable.removeData("disabledByFoundationForm");
        formElemsToEnable.prop("disabled", false);
        this.enabled = true;
        this.fire("enabled");
    }
};


/**
 * @private
 * @param {jQuery.Event} e
 */
Foundation.Form.prototype.submitHandler = function (e) {
    if (this.submitDelegate) {
        e.preventDefault();
    }

    this.element.find(":focus").blur();
    this.setBusyState(true);

    this.element.find(".validationError").removeClass("validationError");
    var validationPromises = [];
    for (var i = 0; i < this.validators.length; i++) {
        var validator = this.validators[i];
        var selector = `[data-validate~=${validator.identifier()}]`;
        var elements = this.element.find(selector).add(this.element.filter(selector));
        validationPromises.push(validator.validate(elements));
    }
    var validation = Foundation.Promise.fromArray(validationPromises, false);

    validation
        .then(function () {
            this.fire("submit");

            // Set the busy state to true again, really just to ensure we're
            // showing the spinner still.
            this.setBusyState(true);

            // Run our commit handler.
            if (this.submitDelegate) {
                this.commitInProgress = this.submitDelegate(this.values());
                this.commitInProgress
                    .otherwise(function () {
                        if (this.get("captcha")) this.get("captcha").refresh();
                        if (this.get("recaptcha")) this.get("recaptcha").refresh();
                        this.setErrorsFromObservable(this.commitInProgress);
                    }.bind(this))
                    .then(function () {
                        this.submitted().marry(this.commitInProgress);
                    }.bind(this))
                    .always(function () {
                        this.commitInProgress = null;
                        this.setBusyState(false);
                    }.bind(this));
            }
        }.bind(this))
        .otherwise(function () {
            this.setBusyState(false);
            this.setErrorsFromObservable(validation);
        }.bind(this));
};

/**
 * @param {Foundation.Observable} obj
 */
Foundation.Form.prototype.setErrorsFromObservable = function (obj) {
    this.errorListView.setErrorsFromObservable(obj);

    // Set the error class on the fields and labels.
    var errors = /** @type {Array} */ (obj.get("errors")) || [];
    var fields = [];
    for (let i = 0; i < errors.length; i++) {
        if (!Foundation.isString(errors[i])) {
            fields = fields.concat(errors[i]["fields"]);
        }
    }
    for (let i = 0; i < fields.length; i++) {
        var field = this.element.find(`[name='${fields[i]}']`);
        field.addClass("validationError");
        this.element.find(`label[for=${field.identify()}]`).addClass("validationError");
    }
};

/**
 * Cancel a submission. This will fail the promise returned by the commit
 * handler if it hasn't yet completed.
 */
Foundation.Form.prototype.cancelSubmit = function () {
    if (this.commitInProgress) this.commitInProgress.tryFail();
};

/**
 * @param {string} name
 * @return {jQuery}
 */
Foundation.Form.prototype.elementForField = function (name) {
    return this.element.find(`[name=${name}]`);
};

/**
 * Find the label text for a field.
 * @param {string} name
 * @return {string}
 */
Foundation.Form.prototype.labelForField = function (name) {
    var fieldId = this.elementForField(name).identify();
    if (fieldId) {
        var label = this.element.find(`label[for=${fieldId}]`);
        return label.text().replace(/\s*:\s*$/, "").trim();
    }
    else {
        return "";
    }
};

/**
 * Handle the change event for the form. We really need redo the
 * way this works. It's inneficient. Users of Foundation.Form
 * should subscribe to specific changes on value names.
 * @param {jQuery.Event} e
 * @private
 */
Foundation.Form.prototype.changeHandler = function () {
    this.fire("change", this.values());
};

/**
 * Resets the form to its original state.
 */
Foundation.Form.prototype.reset = function () {
    this.cancelSubmit();
    this.element[0].reset();
};

/**
 * Destructor.
 * @override
 */
Foundation.Form.prototype.destroy = function () {
    if (this.get("captcha")) this.get("captcha").destroy();
    this.setBusyState(false);
    this.element.off("submit.fdnform")
                .off("change.fdnform");
    Foundation.View.prototype.destroy.call(this);
};

// ---------------------------------------------------------------------

window["Foundation"]["Form"] = Foundation.Form;
window["Foundation"]["Form"].prototype["setSubmitDelegate"] = Foundation.Form.prototype.setSubmitDelegate;
